import type { MonitorInput, MonitorType } from "@shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, ChevronDown, Loader2, ServerOff } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import {
  ApiError,
  createMonitor,
  getMonitor,
  updateMonitor,
} from "@/lib/api";
import { queryKeys, useGroups, useNotificationChannels } from "@/lib/queries";
import { cn } from "@/lib/utils";

const MONITOR_TYPES: Record<MonitorType, string> = {
  http: "HTTP(s)",
  keyword: "HTTP(s) · Keyword",
  ping: "Ping (ICMP)",
  tcp: "TCP Port",
  dns: "DNS",
  push: "Push (heartbeat)",
};

const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"] as const;
const DNS_RECORD_TYPES = ["A", "AAAA", "CNAME", "MX", "TXT", "NS"] as const;

interface MonitorFormState {
  name: string;
  type: MonitorType;
  groupId: string;
  intervalSeconds: string;
  timeoutSeconds: string;
  retries: string;
  retryIntervalSeconds: string;
  url: string;
  httpMethod: string;
  acceptedStatusCodes: string;
  followRedirects: boolean;
  ignoreTls: boolean;
  requestHeaders: string;
  requestBody: string;
  keyword: string;
  keywordInvert: boolean;
  hostname: string;
  port: string;
  dnsRecordType: string;
  dnsResolver: string;
  dnsExpected: string;
  notificationIds: number[];
}

type FieldErrors = Partial<
  Record<"name" | "url" | "hostname" | "port" | "keyword" | "acceptedStatusCodes" | "requestHeaders", string>
>;

const DEFAULT_FORM: MonitorFormState = {
  name: "",
  type: "http",
  groupId: "none",
  intervalSeconds: "60",
  timeoutSeconds: "10",
  retries: "2",
  retryIntervalSeconds: "20",
  url: "",
  httpMethod: "GET",
  acceptedStatusCodes: "200-299",
  followRedirects: true,
  ignoreTls: false,
  requestHeaders: "",
  requestBody: "",
  keyword: "",
  keywordInvert: false,
  hostname: "",
  port: "443",
  dnsRecordType: "A",
  dnsResolver: "",
  dnsExpected: "",
  notificationIds: [],
};

function errorMessage(error: unknown): string {
  return error instanceof ApiError ? error.message : "Could not save monitor";
}

function clampInteger(value: string, fallback: number, min: number, max: number): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, Math.round(parsed)));
}

function FormField({
  id,
  label,
  error,
  hint,
  children,
  className,
}: {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("space-y-2", className)}>
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

function FormSkeleton() {
  return (
    <div className="p-6 md:p-8 space-y-6 max-w-3xl mx-auto w-full">
      <div className="space-y-2">
        <Skeleton className="h-7 w-52" />
        <Skeleton className="h-4 w-80" />
      </div>
      {Array.from({ length: 3 }, (_, index) => (
        <Skeleton key={index} className="h-64 rounded-xl" />
      ))}
    </div>
  );
}

export default function MonitorFormPage() {
  const params = useParams();
  const editing = params.id != null;
  const monitorId = Number(params.id);
  const validMonitorId = Number.isInteger(monitorId) && monitorId > 0;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const groupsQuery = useGroups();
  const notificationsQuery = useNotificationChannels();
  const [form, setForm] = useState<MonitorFormState>(DEFAULT_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});
  const populatedMonitor = useRef<number | null>(null);
  const appliedDefaults = useRef(false);

  const monitorQuery = useQuery({
    queryKey: queryKeys.monitor(monitorId),
    queryFn: () => getMonitor(monitorId),
    enabled: editing && validMonitorId,
  });

  useEffect(() => {
    const monitor = monitorQuery.data;
    if (!monitor || populatedMonitor.current === monitor.id) return;
    populatedMonitor.current = monitor.id;
    setForm({
      name: monitor.name,
      type: monitor.type,
      groupId: monitor.groupId == null ? "none" : String(monitor.groupId),
      intervalSeconds: String(monitor.intervalSeconds),
      timeoutSeconds: String(monitor.timeoutSeconds),
      retries: String(monitor.retries),
      retryIntervalSeconds: String(monitor.retryIntervalSeconds),
      url: monitor.url ?? "",
      httpMethod: monitor.httpMethod,
      acceptedStatusCodes: monitor.acceptedStatusCodes.join(", "),
      followRedirects: monitor.followRedirects,
      ignoreTls: monitor.ignoreTls,
      requestHeaders: monitor.requestHeaders ? JSON.stringify(monitor.requestHeaders, null, 2) : "",
      requestBody: monitor.requestBody ?? "",
      keyword: monitor.keyword ?? "",
      keywordInvert: monitor.keywordInvert,
      hostname: monitor.hostname ?? "",
      port: monitor.port == null ? "" : String(monitor.port),
      dnsRecordType: monitor.dnsRecordType,
      dnsResolver: monitor.dnsResolver ?? "",
      dnsExpected: monitor.dnsExpected ?? "",
      notificationIds: monitor.notificationIds,
    });
  }, [monitorQuery.data]);

  useEffect(() => {
    if (editing || appliedDefaults.current || !notificationsQuery.data) return;
    appliedDefaults.current = true;
    setForm(current => ({
      ...current,
      notificationIds: notificationsQuery.data
        .filter(channel => channel.isDefault)
        .map(channel => channel.id),
    }));
  }, [editing, notificationsQuery.data]);

  const saveMutation = useMutation({
    mutationFn: (input: MonitorInput) =>
      editing ? updateMonitor(monitorId, input) : createMonitor(input),
    onSuccess: async result => {
      const invalidations = [
        queryClient.invalidateQueries({ queryKey: queryKeys.monitors }),
      ];
      if (editing) {
        invalidations.push(queryClient.invalidateQueries({ queryKey: queryKeys.monitor(monitorId) }));
      }
      await Promise.all(invalidations);
      toast.success(editing ? "Monitor updated" : "Monitor created");
      navigate(`/monitors/${result.id}`);
    },
    onError: error => toast.error(errorMessage(error)),
  });

  const setField = <K extends keyof MonitorFormState,>(key: K, value: MonitorFormState[K]) => {
    setForm(current => ({ ...current, [key]: value }));
    if (key in errors) {
      setErrors(current => {
        const next = { ...current };
        delete next[key as keyof FieldErrors];
        return next;
      });
    }
  };

  const setMonitorType = (value: string) => {
    if (
      value === "http" ||
      value === "keyword" ||
      value === "ping" ||
      value === "tcp" ||
      value === "dns" ||
      value === "push"
    ) {
      setField("type", value);
    }
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: FieldErrors = {};
    const name = form.name.trim();
    const needsUrl = form.type === "http" || form.type === "keyword";
    const needsHostname = form.type === "ping" || form.type === "tcp" || form.type === "dns";

    if (!name) nextErrors.name = "Name is required.";
    if (needsUrl && !form.url.trim()) nextErrors.url = "URL is required.";
    if (needsHostname && !form.hostname.trim()) nextErrors.hostname = "Hostname is required.";
    if (form.type === "tcp" && !form.port.trim()) nextErrors.port = "Port is required.";
    if (form.type === "keyword" && !form.keyword.trim()) nextErrors.keyword = "Keyword is required.";

    const acceptedStatusCodes = form.acceptedStatusCodes
      .split(",")
      .map(code => code.trim())
      .filter(Boolean);
    if (needsUrl && (
      acceptedStatusCodes.length === 0 ||
      acceptedStatusCodes.some(code => !/^\d{3}(-\d{3})?$/.test(code))
    )) {
      nextErrors.acceptedStatusCodes = "Use codes like 200 or ranges like 200-299.";
    }

    let requestHeaders: Record<string, string> | null = null;
    if (needsUrl && form.requestHeaders.trim()) {
      try {
        const parsed: unknown = JSON.parse(form.requestHeaders);
        if (parsed == null || typeof parsed !== "object" || Array.isArray(parsed)) {
          nextErrors.requestHeaders = "Headers must be a JSON object.";
        } else {
          const headers: Record<string, string> = {};
          for (const [key, value] of Object.entries(parsed)) {
            if (typeof value !== "string") {
              nextErrors.requestHeaders = "Every header value must be a string.";
              break;
            }
            headers[key] = value;
          }
          if (!nextErrors.requestHeaders) requestHeaders = headers;
        }
      } catch {
        nextErrors.requestHeaders = "Headers must be valid JSON.";
      }
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      toast.error("Check the highlighted fields");
      return;
    }

    const intervalSeconds = clampInteger(form.intervalSeconds, 60, 20, 86_400);
    const timeoutSeconds = clampInteger(form.timeoutSeconds, 10, 1, 300);
    const retries = clampInteger(form.retries, 2, 0, 10);
    const retryIntervalSeconds = clampInteger(form.retryIntervalSeconds, 20, 5, 3_600);
    const port = form.type === "tcp" ? clampInteger(form.port, 443, 1, 65_535) : null;
    setForm(current => ({
      ...current,
      intervalSeconds: String(intervalSeconds),
      timeoutSeconds: String(timeoutSeconds),
      retries: String(retries),
      retryIntervalSeconds: String(retryIntervalSeconds),
      port: port == null ? current.port : String(port),
    }));

    const input: MonitorInput = {
      name,
      type: form.type,
      url: needsUrl ? form.url.trim() : null,
      hostname: needsHostname ? form.hostname.trim() : null,
      port,
      intervalSeconds,
      timeoutSeconds,
      retries,
      retryIntervalSeconds,
      httpMethod: form.httpMethod,
      acceptedStatusCodes,
      requestHeaders,
      requestBody: needsUrl && form.requestBody ? form.requestBody : null,
      followRedirects: form.followRedirects,
      ignoreTls: form.ignoreTls,
      keyword: form.type === "keyword" ? form.keyword.trim() : null,
      keywordInvert: form.keywordInvert,
      dnsRecordType: form.dnsRecordType,
      dnsResolver: form.type === "dns" && form.dnsResolver.trim() ? form.dnsResolver.trim() : null,
      dnsExpected: form.type === "dns" && form.dnsExpected.trim() ? form.dnsExpected.trim() : null,
      groupId: form.groupId === "none" ? null : Number(form.groupId),
      notificationIds: form.notificationIds,
    };
    saveMutation.mutate(input);
  };

  if (editing && monitorQuery.isLoading) return <FormSkeleton />;
  if (editing && (!validMonitorId || monitorQuery.isError)) {
    return (
      <div className="p-6 md:p-8 space-y-6 max-w-3xl mx-auto w-full">
        <EmptyState
          icon={ServerOff}
          title="Monitor not found"
          description="This monitor may have been deleted or the link is invalid."
          action={
            <Button type="button" variant="outline" onClick={() => navigate("/dashboard")}>
              Back to dashboard
            </Button>
          }
        />
      </div>
    );
  }

  const httpTarget = form.type === "http" || form.type === "keyword";
  const hostnameTarget = form.type === "ping" || form.type === "tcp" || form.type === "dns";

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-3xl mx-auto w-full">
      <PageHeader
        title={editing ? "Edit monitor" : "New monitor"}
        description={
          editing
            ? "Update how this monitor checks its target."
            : "Configure a target and start monitoring it."
        }
      />

      <form onSubmit={submit} className="space-y-6" noValidate>
        <Card>
          <CardHeader>
            <CardTitle>Basics</CardTitle>
            <CardDescription>Name the monitor and choose its check schedule.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <FormField id="monitor-name" label="Name" error={errors.name}>
              <Input
                id="monitor-name"
                value={form.name}
                placeholder="Production website"
                aria-invalid={Boolean(errors.name)}
                onChange={event => setField("name", event.target.value)}
              />
            </FormField>

            <div className="grid gap-5 sm:grid-cols-2">
              <FormField id="monitor-type" label="Type">
                <Select value={form.type} onValueChange={setMonitorType} disabled={editing}>
                  <SelectTrigger id="monitor-type" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(MONITOR_TYPES).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField id="monitor-group" label="Group">
                <Select
                  value={form.groupId}
                  onValueChange={value => {
                    // Radix re-syncs its hidden native <select> when our value changes;
                    // if the async group options have not registered yet the assignment
                    // does not stick and it echoes back "". Ignore that, keep the value.
                    if (value) setField("groupId", value);
                  }}
                >
                  <SelectTrigger id="monitor-group" className="w-full">
                    <SelectValue placeholder="No group" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No group</SelectItem>
                    {groupsQuery.data?.map(group => (
                      <SelectItem key={group.id} value={String(group.id)}>{group.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <FormField
                id="monitor-interval"
                label="Interval (seconds)"
                hint={form.type === "push" ? "Expected heartbeat period" : "How often to check"}
              >
                <Input
                  id="monitor-interval"
                  type="number"
                  min={20}
                  max={86400}
                  value={form.intervalSeconds}
                  onChange={event => setField("intervalSeconds", event.target.value)}
                />
              </FormField>
              {form.type !== "push" && (
                <FormField id="monitor-timeout" label="Timeout (seconds)">
                  <Input
                    id="monitor-timeout"
                    type="number"
                    min={1}
                    max={300}
                    value={form.timeoutSeconds}
                    onChange={event => setField("timeoutSeconds", event.target.value)}
                  />
                </FormField>
              )}
              <FormField id="monitor-retries" label="Retries">
                <Input
                  id="monitor-retries"
                  type="number"
                  min={0}
                  max={10}
                  value={form.retries}
                  onChange={event => setField("retries", event.target.value)}
                />
              </FormField>
              <FormField id="monitor-retry-interval" label="Retry interval (seconds)">
                <Input
                  id="monitor-retry-interval"
                  type="number"
                  min={5}
                  max={3600}
                  value={form.retryIntervalSeconds}
                  onChange={event => setField("retryIntervalSeconds", event.target.value)}
                />
              </FormField>
            </div>
          </CardContent>
        </Card>

        {form.type !== "push" && (
          <Card>
            <CardHeader>
              <CardTitle>Target</CardTitle>
              <CardDescription>Define the endpoint and what a successful check looks like.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {httpTarget && (
                <>
                  <FormField id="monitor-url" label="URL" error={errors.url}>
                    <Input
                      id="monitor-url"
                      type="url"
                      value={form.url}
                      placeholder="https://example.com"
                      aria-invalid={Boolean(errors.url)}
                      onChange={event => setField("url", event.target.value)}
                    />
                  </FormField>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <FormField id="monitor-method" label="Method">
                      <Select value={form.httpMethod} onValueChange={value => setField("httpMethod", value)}>
                        <SelectTrigger id="monitor-method" className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {HTTP_METHODS.map(method => (
                            <SelectItem key={method} value={method}>{method}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormField>
                    <FormField
                      id="monitor-status-codes"
                      label="Accepted status codes"
                      error={errors.acceptedStatusCodes}
                    >
                      <Input
                        id="monitor-status-codes"
                        value={form.acceptedStatusCodes}
                        placeholder="200-299"
                        aria-invalid={Boolean(errors.acceptedStatusCodes)}
                        onChange={event => setField("acceptedStatusCodes", event.target.value)}
                      />
                    </FormField>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="flex items-center justify-between rounded-lg border p-3">
                      <Label htmlFor="monitor-follow-redirects">Follow redirects</Label>
                      <Switch
                        id="monitor-follow-redirects"
                        checked={form.followRedirects}
                        onCheckedChange={checked => setField("followRedirects", checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between rounded-lg border p-3">
                      <Label htmlFor="monitor-ignore-tls">Ignore TLS errors</Label>
                      <Switch
                        id="monitor-ignore-tls"
                        checked={form.ignoreTls}
                        onCheckedChange={checked => setField("ignoreTls", checked)}
                      />
                    </div>
                  </div>

                  {form.type === "keyword" && (
                    <div className="grid gap-5 sm:grid-cols-2">
                      <FormField id="monitor-keyword" label="Keyword" error={errors.keyword}>
                        <Input
                          id="monitor-keyword"
                          value={form.keyword}
                          aria-invalid={Boolean(errors.keyword)}
                          onChange={event => setField("keyword", event.target.value)}
                        />
                      </FormField>
                      <FormField id="monitor-keyword-rule" label="Match rule">
                        <Select
                          value={form.keywordInvert ? "absent" : "present"}
                          onValueChange={value => setField("keywordInvert", value === "absent")}
                        >
                          <SelectTrigger id="monitor-keyword-rule" className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="present">must contain</SelectItem>
                            <SelectItem value="absent">must NOT contain</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormField>
                    </div>
                  )}

                  <Collapsible>
                    <CollapsibleTrigger asChild>
                      <Button type="button" variant="ghost" className="px-0 text-muted-foreground">
                        Advanced request options
                        <ChevronDown className="size-4" />
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-5 pt-3">
                      <FormField
                        id="monitor-headers"
                        label="Request headers (JSON)"
                        error={errors.requestHeaders}
                      >
                        <Textarea
                          id="monitor-headers"
                          value={form.requestHeaders}
                          placeholder={'{\\n  \"Authorization\": \"Bearer …\"\\n}'}
                          className="min-h-28 font-mono text-xs"
                          aria-invalid={Boolean(errors.requestHeaders)}
                          onChange={event => setField("requestHeaders", event.target.value)}
                        />
                      </FormField>
                      <FormField id="monitor-body" label="Request body">
                        <Textarea
                          id="monitor-body"
                          value={form.requestBody}
                          className="min-h-28 font-mono text-xs"
                          onChange={event => setField("requestBody", event.target.value)}
                        />
                      </FormField>
                    </CollapsibleContent>
                  </Collapsible>
                </>
              )}

              {hostnameTarget && (
                <div className={cn("grid gap-5", form.type === "tcp" && "sm:grid-cols-2")}>
                  <FormField id="monitor-hostname" label="Hostname" error={errors.hostname}>
                    <Input
                      id="monitor-hostname"
                      value={form.hostname}
                      placeholder="example.com"
                      aria-invalid={Boolean(errors.hostname)}
                      onChange={event => setField("hostname", event.target.value)}
                    />
                  </FormField>
                  {form.type === "tcp" && (
                    <FormField id="monitor-port" label="Port" error={errors.port}>
                      <Input
                        id="monitor-port"
                        type="number"
                        min={1}
                        max={65535}
                        value={form.port}
                        aria-invalid={Boolean(errors.port)}
                        onChange={event => setField("port", event.target.value)}
                      />
                    </FormField>
                  )}
                </div>
              )}

              {form.type === "dns" && (
                <>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <FormField id="monitor-dns-type" label="Record type">
                      <Select value={form.dnsRecordType} onValueChange={value => setField("dnsRecordType", value)}>
                        <SelectTrigger id="monitor-dns-type" className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DNS_RECORD_TYPES.map(recordType => (
                            <SelectItem key={recordType} value={recordType}>{recordType}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormField>
                    <FormField id="monitor-dns-resolver" label="Resolver (optional)">
                      <Input
                        id="monitor-dns-resolver"
                        value={form.dnsResolver}
                        placeholder="1.1.1.1"
                        onChange={event => setField("dnsResolver", event.target.value)}
                      />
                    </FormField>
                  </div>
                  <FormField id="monitor-dns-expected" label="Expected value (optional)">
                    <Input
                      id="monitor-dns-expected"
                      value={form.dnsExpected}
                      onChange={event => setField("dnsExpected", event.target.value)}
                    />
                  </FormField>
                </>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Notifications</CardTitle>
            <CardDescription>Choose which channels should receive status changes.</CardDescription>
          </CardHeader>
          <CardContent>
            {notificationsQuery.isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 2 }, (_, index) => (
                  <Skeleton key={index} className="h-12 w-full" />
                ))}
              </div>
            ) : notificationsQuery.data?.length ? (
              <div className="space-y-2">
                {notificationsQuery.data.map(channel => {
                  const checked = form.notificationIds.includes(channel.id);
                  return (
                    <label
                      key={channel.id}
                      className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 hover:bg-muted/40"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={value =>
                          setField(
                            "notificationIds",
                            value === true
                              ? [...form.notificationIds, channel.id]
                              : form.notificationIds.filter(id => id !== channel.id),
                          )
                        }
                      />
                      <span className="min-w-0 flex-1 truncate text-sm font-medium">{channel.name}</span>
                      {channel.isDefault && <span className="text-xs text-muted-foreground">Default</span>}
                      <Badge variant="outline" className="text-[10px] font-mono uppercase">
                        {channel.type}
                      </Badge>
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-center gap-3 rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                <Bell className="size-4 shrink-0" />
                <span>
                  No notification channels.{" "}
                  <Link to="/notifications" className="font-medium text-foreground underline underline-offset-4">
                    Add one
                  </Link>
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="sticky bottom-0 z-10 flex items-center justify-end gap-2 rounded-xl border bg-background/95 p-4 shadow-lg backdrop-blur">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} disabled={saveMutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={saveMutation.isPending}>
            {saveMutation.isPending && <Loader2 className="size-4 animate-spin" />}
            {editing ? "Save changes" : "Save monitor"}
          </Button>
        </div>
      </form>
    </div>
  );
}
